import React, { useState } from "react";
import { useMemo } from "react";
import ImageCard from "./ImageCard";
import {
  Grid,
  Input,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";

import ImageCardGroup from "./ImageCardGroup";
import { closeModal } from "../../store/modal/actions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";

const FlatView = (props: FlatViewProps) => {
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState("");
  const [opencopyURLmessage, setOpenCopyURLmessage] = useState(false);
  const state = useSelector((state: RootState) => state);
  const dispatch = useDispatch();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value as string);
  };

  const handleDeleteAsset = (filePath: string) => {
    const updatedAssets = props.assets.filter(
      (asset) => asset.physical_file_path !== filePath
    );
    props.updateAssets(updatedAssets);
  };

  const handleDeleteGroupAssets = (filePaths: string[]) => {
    const updatedAssets = props.assets.filter(
      (asset) => !filePaths.includes(asset.physical_file_path)
    );
    props.updateAssets(updatedAssets);
  };

  const handleCopyToClipboard = (url: string) => {
    if (!props.isBannerImageSelectModal) {
      //this function has to do nothing if it is a child of the BannerImageSelectorModal
      setOpenCopyURLmessage(true);
      setTimeout(() => {
        if (state.modal_reducer.isOpen) {
          dispatch(closeModal());
        }
        setOpenCopyURLmessage(false); // Hide the message after 3 seconds since there is no user interaction with this message
      }, 1000);
      navigator.clipboard.writeText(url);
    }
  };

  // Group images by group_id based on the search term. if no search term, it uses all images
  const groupedFilteredImages = useMemo(() => {
    const groups = [] as { groupId: string | null; items: AssetItem[] }[];
    const groupedById = new Map<string, AssetItem[]>();
    const ungrouped: AssetItem[] = [];

    // removes the images/ from the name. We have to do this because the FlatView and the FolderView receives different values for the name. This snippet makes sure both components have the same value in the name property and the delete function works for both.
    const renamedFilteredAssets = props.assets.map((asset) => ({
      ...asset, // Spread the existing properties
      name: asset.name.startsWith("images/")
        ? asset.name.replace("images/", "")
        : asset.name,
    }));

    // Filter and group assets based on the search value
    const filteredAssets = renamedFilteredAssets.filter((asset: any) => {
      return asset.name.includes(searchValue); // returns all assets if no search term
    });

    // Iterate over filtered assets and group them by group_id
    filteredAssets.forEach((image: any) => {
      const groupId = image.metadata?.group_id || null;
      if (groupId) {
        // Group by group_id
        if (!groupedById.has(groupId)) {
          groupedById.set(groupId, []);
        }
        groupedById.get(groupId)?.push(image);
      } else {
        // Ungrouped images
        ungrouped.push(image);
      }
    });

    // Convert the map into an array of groups
    groupedById.forEach((items, groupId) => {
      groups.push({ groupId, items });
    });

    // Add ungrouped images as a separate group
    if (ungrouped.length > 0) {
      groups.push({ groupId: null, items: ungrouped });
    }

    return groups;
  }, [props.assets, searchValue]);

  return (
    <>
      <Input
        style={{ marginBottom: 30 }}
        placeholder="Search"
        onChange={handleChange}
        value={searchValue}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
      />
      <Grid container spacing={2}>
        {/* Loop through grouped filtered images and render them */}
        {groupedFilteredImages.map((group) =>
          group.groupId ? (
            // Render ImageCardGroup if group has a groupId
            <Grid item xs={12} sm={6} md={4} lg={3} key={group.groupId}>
              <ImageCardGroup
                imageUrl={group.items[0].url} // uses the '-large' image
                imageFilePath={group.items[0].physical_file_path}
                imageName={group.items[0].name}
                setSelectedImage={props.setSelectedImage}
                selectedImageUrl={props.selectedImageUrl}
                handleDeleteGroupAssets={handleDeleteGroupAssets}
                handleCopyToClipboard={handleCopyToClipboard}
                group={group.items} // fix this, seems not to be passing the correct object
                isBannerImageSelectModal={props.isBannerImageSelectModal}
              />
            </Grid>
          ) : (
            // Render ImageCard for ungrouped images
            group.items.map((asset: any) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={asset.id}
                className={classes.card}
              >
                <ImageCard
                  imageFilePath={asset.physical_file_path}
                  imageName={asset.name}
                  imageMeta={asset.metadata}
                  imageUrl={asset.url}
                  groupId={asset.group_id}
                  selectedImageUrl={props.selectedImageUrl}
                  setSelectedImage={props.setSelectedImage}
                  handleDeleteAsset={handleDeleteAsset}
                  handleCopyToClipboard={handleCopyToClipboard}
                />
              </Grid>
            ))
          )
        )}
      </Grid>

      {/* copy url confirmation message */}
      <Dialog open={opencopyURLmessage}>
        <DialogTitle>{"File URL Copied!"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The URL has been copied to the clipboard
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface FlatViewProps {
  assets: any[];
  setSelectedImage?: (url: string) => void;
  selectedImageUrl?: string;
  updateAssets: (updatedAssets: any[]) => void;
  isBannerImageSelectModal?: boolean;
}

interface AssetItem {
  created_at: string;
  id: string;
  metadata: Metadata;
  name: string;
  physical_file_path: string;
  updated_at: string;
  url: string;
}
interface Metadata {
  group_id?: string; // Assuming group_id is optional
}

const useStyles = makeStyles((theme) => ({
  card: {
    maxWidth: "",
  },
}));

export default FlatView;
